import type { EventStatus, ProgramEventStatus } from 'models';
import { type ReactNode, useMemo } from 'react';
import { type AlertType, CalendarTimeAddFillIcon, Document3FillIcon, TimeFilledIcon } from 'ui';

type AlertMessageContent = {
  description?: string;
  icon?: ReactNode;
  title: string;
  type: AlertType;
};

export const useGetAlertMessageContent = ({
  eventStatus,
  status,
}: {
  eventStatus: EventStatus | ProgramEventStatus;
  status?: 'approved' | 'denied' | 'expired' | 'pending';
}): AlertMessageContent | null =>
  useMemo(() => {
    if (eventStatus === 'draft') {
      return {
        description: 'This program event is in an object that has not been published yet.',
        icon: <Document3FillIcon />,
        title: 'Not published',
        type: 'neutral',
      };
    }

    if (eventStatus === 'published') {
      if (status === 'approved') {
        return {
          description: 'This event is allowed since the admin has approved exceeding the member limit.',
          title: 'Approved',
          type: 'success',
        };
      }

      return null;
    }

    switch (status) {
      case 'approved':
      case undefined:
        return null;
      case 'denied':
        return {
          description: 'We cannot allow this event because the number of invited members is exceeded.',
          title: 'Declined by admin',
          type: 'error',
        };
      case 'expired':
        return {
          description: 'Admin didn’t approve the event in time.',
          icon: <CalendarTimeAddFillIcon />,
          title: 'Past due',
          type: 'neutral',
        };
      case 'pending':
        return {
          description: 'We’re waiting for admin to approve',
          icon: <TimeFilledIcon />,
          title: 'Pending',
          type: 'warning',
        };
    }
  }, [eventStatus, status]);
